<script lang="ts">
  // libs
  import { onMount } from "svelte";
  import { authorizationCheck } from "@shared/svelte/Modules/AuthorizationCheck.svelte";
  import * as LocaleString from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";

  // components
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import AddPaymentInstrumentModal from "@retailer/svelte/Payments/AddPaymentInstrumentModal.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import PayInvoicesModalBannerSelector from "@retailer/svelte/Payments/Banners/selectors/PayInvoicesModalBannerSelector.svelte";

  // services
  import * as PaymentInstrumentService from "@retailer/services/payment_instrument_service";
  import * as CreditsService from "@retailer/services/credits_service";

  // stores
  import { paymentInstrumentsStore } from "@retailer/svelte/stores/PaymentInstrumentStore";
  import { creditsStore } from "@retailer/stores/CreditsStore";
  import { InvoiceStore } from "@retailer/stores/InvoiceStore";

  // types
  import type { Invoice, PaymentInstrumentType } from "@shared/types/Invoice";
  import type { PaymentInstrument } from "@shared/types/PaymentInstrument";
  import type { AvailableCredit, CreditToApply } from "@shared/types/Credit";

  // props
  export let invoices: Invoice[] = [];
  export let scheduledDate: string;
  export let onSchedulePayment: (
    paymentInstrument: PaymentInstrumentType | string,
    scheduledDate: string,
    creditDistributorIds: number[],
    selectedCredits: { [k: number]: number[] },
  ) => void;
  export let shouldShowPayInvoicesModal: boolean = false;
  export let togglePayInvoicesModal: () => void;
  export let enteredMicrodepositFlow: boolean = false;

  // data
  let selectedPaymentInstrument: PaymentInstrumentType | null;
  let showModals: { [key in string]: boolean } = { ChoosePaymentTypeModal: false };

  // distributorCreditLists is used to hold the specifically selected credits
  // keys to distributorCreditLists are the distributor names
  let distributorCreditLists: { [k: string]: CreditToApply[] } = {};

  // functions
  const handlePaymentInstrumentSelection = e => {
    if (e.target.value === "newPaymentInstrument") {
      showAddPaymentModal();
    }
  };

  const showAddPaymentModal = () => {
    showModals["ChoosePaymentTypeModal"] = true;
    shouldShowPayInvoicesModal = false;
  };

  const onClosePaymentMethodModals = () => {
    showModals["ChoosePaymentTypeModal"] = false;
    shouldShowPayInvoicesModal = true;
  };

  const onSavePaymentInstrument = async (paymentInstrument: PaymentInstrument) => {
    onClosePaymentMethodModals();
    selectedPaymentInstrument = paymentInstrument;
    shouldShowPayInvoicesModal = true;
  };

  const maxUsableCredits = (credits: AvailableCredit[]): AvailableCredit[] => {
    const totals = invoiceTotalByDistributor();

    return credits.reduce<AvailableCredit[]>((maxCredits, availableCreditInfo) => {
      const distributorId = availableCreditInfo.distributor_id;
      // Skip the credit if they do not have an invoice selected from the credit's distributor
      if (
        invoices.some(invoice => invoice.distributor_id == distributorId) &&
        parseFloat(availableCreditInfo.amount) > 0
      ) {
        const defaultToSelected = !invoices.some(
          invoice => invoice.distributor_id == distributorId && !invoice.use_credit_by_default,
        );

        const creditAmount =
          -1 *
          Math.min(
            parseFloat(availableCreditInfo.amount),
            totals[availableCreditInfo.distributor_name],
          );

        maxCredits.push({
          distributor_id: distributorId,
          distributor_name: availableCreditInfo.distributor_name,
          amount: creditAmount.toString(),
          selected: defaultToSelected,
        });
      }

      return maxCredits;
    }, [] as AvailableCredit[]);
  };

  const invoiceTotalByDistributor = (): { [k in number]: number } => {
    return invoices.reduce((totals, invoice) => {
      if (!totals[invoice.distributor.name]) totals[invoice.distributor.name] = 0;

      totals[invoice.distributor.name] += parseFloat(invoice.amount_due);

      return totals;
    }, {});
  };

  const calculateTotalCreditsApplied: (
    credits: AvailableCredit[],
    distributorCreditLists: { [k: string]: CreditToApply[] },
  ) => number = (credits, distributorCreditLists) => {
    // unspecifiedCreditsTotal will be <= 0
    const unspecifiedCreditsTotal = credits.reduce((total: number, creditInfo: AvailableCredit) => {
      if (!creditInfo.selected) return total;

      return total - parseFloat(creditInfo.amount);
    }, 0);

    const invoiceTotals = invoiceTotalByDistributor();
    const creditTotals = {};

    // specifiedCreditsTotal will be <= 0
    const specifiedCreditsTotal = Object.values(distributorCreditLists)
      .flat()
      .reduce((total: number, credit: CreditToApply) => {
        if (!credit.selected) return total;
        if (!creditTotals[credit.distributor_name]) creditTotals[credit.distributor_name] = 0;

        const creditAmountToApply = parseFloat(credit.remaining_credit) * -1;

        // If applying this credit would cause more credit to be applied than the user owes, set the total applied credit to the total amount due
        if (
          creditTotals[credit.distributor_name] + creditAmountToApply >
          invoiceTotals[credit.distributor_name]
        ) {
          const added =
            invoiceTotals[credit.distributor_name] - creditTotals[credit.distributor_name];

          creditTotals[credit.distributor_name] = invoiceTotals[credit.distributor_name];
          return total + added;
        }

        creditTotals[credit.distributor_name] =
          creditTotals[credit.distributor_name] + creditAmountToApply;

        return total + creditAmountToApply;
      }, 0);

    return unspecifiedCreditsTotal + specifiedCreditsTotal;
  };

  const calculateInvoicesTotal = invoices => {
    return invoices.reduce((agg, invoice) => {
      return agg + parseFloat(invoice.amount_due);
    }, 0);
  };

  const onSchedulePaymentWrapper = () => {
    const selectedDistributorIds = usableCredits.reduce((agg, creditInfo) => {
      if (creditInfo.selected) return [...agg, creditInfo.distributor_id];
      return agg;
    }, []);

    const selectedDistributorCredits = {};

    Object.keys(distributorCreditLists).forEach(distributorName => {
      distributorCreditLists[distributorName].forEach(distributorCredit => {
        if (!distributorCredit.selected) return;
        if (!selectedDistributorCredits[distributorCredit.distributor_id]) {
          selectedDistributorCredits[distributorCredit.distributor_id] = [];
        }

        selectedDistributorCredits[distributorCredit.distributor_id].push(distributorCredit.id);
      });
    });

    onSchedulePayment(
      selectedPaymentInstrument,
      userSelectedDate,
      selectedDistributorIds,
      selectedDistributorCredits,
    );
  };

  const isScheduledDateLaterThanDueDate = (scheduledDate: string, dueDate: string) => {
    return (
      Date.parse(
        LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(scheduledDate),
      ) > Date.parse(LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(dueDate))
    );
  };

  const getEarliestDueDateFromInvoices = (invoices: Invoice[]) => {
    return invoices.length === 0
      ? null
      : invoices.reduce((c, n) => (Date.parse(n.due_date) < Date.parse(c.due_date) ? n : c))
          .due_date;
  };

  const invoicesNotPayableAfterDueDate = (invoices: Invoice[]) => {
    return invoices.filter(invoice => !invoice.is_payable_after_due_date);
  };

  const distributorsByCreditMode = (invoices: Invoice[]) => {
    const distributorsWithSpecifiedCredits = new Set<number>();
    const distributorsWithDefaultCredits = new Set<number>();
    invoices.forEach(i => {
      if (i.use_credit_by_default) distributorsWithDefaultCredits.add(i.distributor_id);
      else distributorsWithSpecifiedCredits.add(i.distributor_id);
    });

    return {
      withSpecifiedCredits: Array.from(distributorsWithSpecifiedCredits),
      withDefaultCredits: Array.from(distributorsWithDefaultCredits),
    };
  };

  const getApplicableCredits = async (invoices: Invoice[]) => {
    const distributors = distributorsByCreditMode(invoices);
    if (distributors.withDefaultCredits.length > 0) CreditsService.getAvailable();
    if (distributors.withSpecifiedCredits.length > 0) {
      distributorCreditLists = await CreditsService.getCreditsWithAmounts(
        distributors.withSpecifiedCredits,
      );
    }
  };

  // lifecycle
  onMount(() => {
    (async () => {
      await PaymentInstrumentService.get();
      selectedPaymentInstrument = $paymentInstrumentsStore.paymentInstruments.find(
        instrument => instrument.default_instrument == true,
      );
    })();
  });

  // reactives
  $: usableCredits =
    $creditsStore.loaded.available && invoices.length > 0
      ? maxUsableCredits($creditsStore.available)
      : [];

  // selectedCreditsTotal is the sum of the selected credits to be used
  $: selectedCreditsTotal = calculateTotalCreditsApplied(usableCredits, distributorCreditLists);

  // paymentTotal is the amount the user must pay after credits are applied
  $: paymentTotal = invoicesTotal - selectedCreditsTotal;

  // invoicesTotal is the sum of the invoices without credits applied
  $: invoicesTotal = calculateInvoicesTotal(invoices);
  $: title = singleInvoice ? "Pay Invoice" : "Pay Selected";
  $: if (enteredMicrodepositFlow) {
    shouldShowPayInvoicesModal = false;
  }
  $: paidInvoices = $InvoiceStore.loaded ? $InvoiceStore.counts.paid : 0;
  $: userSelectedDate = scheduledDate;
  $: singleInvoice = invoices.length == 1;
  $: earliestDue = getEarliestDueDateFromInvoices(invoices);
  $: invoicesNotPayableLate = invoicesNotPayableAfterDueDate(invoices);
  $: earliestDueNotPayableAfterDueDate = getEarliestDueDateFromInvoices(invoicesNotPayableLate);
  $: invoiceNotPayable =
    invoicesNotPayableLate.length > 0 &&
    isScheduledDateLaterThanDueDate(userSelectedDate, earliestDueNotPayableAfterDueDate);
  $: showWarning = isScheduledDateLaterThanDueDate(userSelectedDate, earliestDue);
  $: getApplicableCredits(invoices);
</script>

<Modal
  bind:show={shouldShowPayInvoicesModal}
  closeOnBackgroundClick={false}
  size="medium"
  fizzModal={true}
  overflow={"auto"}
>
  <div class="fizz-inset-square-24" id="pay-with-credits-modal">
    <header class="fizz-space-between fizz-stack-16">
      <h4 class="fizz-heading-4">{title}</h4>
      <div on:click|preventDefault={togglePayInvoicesModal}>
        <Icon type="X" extraClassNames="fizz-text-brand cursor-pointer" height="16" width="16" />
      </div>
    </header>

    <PayInvoicesModalBannerSelector
      {singleInvoice}
      showNotPayableError={invoiceNotPayable}
      {showWarning}
    />

    <div class="fizz-flex-col-responsive">
      <!-- Accordians & Total -->
      <div class="fizz-card fizz-bordered fizz-stack-24">
        <!-- Invoices accordian -->
        <div class="fizz-border-bottom">
          <details class="fizz-accordion">
            <summary>
              <div class="fizz-space-between">
                <span class="fizz-heading-4">Invoice total ({invoices.length} Invoices)</span>
                <span data-testid="invoices-total-amount"
                  ><strong><FormatMoney input={invoicesTotal} formatType={"5"} /></strong></span
                >
              </div>
            </summary>
            <div style="padding-right: 3rem">
              <table class="fizz-text-small">
                <tbody>
                  {#each invoices as invoiceRow}
                    <tr>
                      <td class="fizz-no-wrap fizz-inset-square-16">
                        {invoiceRow.distributor.name}
                      </td>

                      <td class="fizz-text-left fizz-full-width">
                        #{invoiceRow.invoice_reference_id}
                      </td>

                      <td class="fizz-text-right fizz-no-wrap">
                        <FormatMoney input={invoiceRow.amount_due} formatType={"5"} />
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </details>
        </div>

        <!-- Credits accordian -->
        <div class="fizz-border-bottom">
          <details class="fizz-accordion">
            <summary>
              <div class="fizz-space-between">
                <span class="fizz-heading-4">Credits applied</span>
                <span id="credits-total-amount"
                  ><strong
                    ><FormatMoney input={-1 * selectedCreditsTotal} formatType={"5"} /></strong
                  ></span
                >
              </div>
            </summary>
            {#if usableCredits.length > 0}
              {#each usableCredits as creditInfo, i}
                {#if !distributorCreditLists[creditInfo.distributor_name]}
                  <div class="fizz-space-between fizz-inset-square-16">
                    <span
                      class:fizz-text-interactive={!creditInfo.selected}
                      class="fizz-text-small"
                    >
                      {#if !creditInfo.selected}
                        Apply
                      {/if}
                      {creditInfo.distributor_name} credits

                      {#if !creditInfo.selected}
                        (<FormatMoney input={parseFloat(creditInfo.amount)} formatType={"5"} />)
                      {/if}
                      {#if creditInfo.selected}
                        <span class="fizz-text-small">
                          <FormatMoney input={parseFloat(creditInfo.amount)} formatType={"5"} />
                        </span>
                        <Icon
                          id={"remove-credit-" + i}
                          type="X"
                          width="14"
                          height="14"
                          extraStyles="cursor: pointer"
                          onPress={() => (creditInfo.selected = false)}
                        />
                      {:else}
                        <Icon
                          id={"add-credit-" + i}
                          type="PlusBlack"
                          width="14"
                          height="14"
                          extraStyles="cursor: pointer"
                          onPress={() => (creditInfo.selected = true)}
                        />
                      {/if}
                    </span>
                  </div>
                {/if}
              {/each}
            {/if}
            {#each Object.keys(distributorCreditLists) as distributorName}
              {#each distributorCreditLists[distributorName] as credit, i}
                <div
                  class="fizz-text-small fizz-flex-row fizz-space-between fizz-inset-square-16"
                  class:fizz-text-interactive={!credit.selected}
                >
                  <div>{credit.distributor_name}</div>
                  <div>#{credit.invoice_reference_id} </div>
                  <div class="fizz-flex-row">
                    <div><FormatMoney input={credit.remaining_credit} formatType={"5"} /></div>
                    {#if credit.selected}
                      <Icon
                        id={"remove-credit-" + i}
                        type="X"
                        width="14"
                        height="14"
                        extraStyles="cursor: pointer"
                        onPress={() => (credit.selected = false)}
                      />
                    {:else}
                      <Icon
                        id={"add-credit-" + i}
                        type="PlusBlack"
                        width="14"
                        height="14"
                        extraStyles="cursor: pointer"
                        onPress={() => (credit.selected = true)}
                      />
                    {/if}
                  </div>
                </div>
              {/each}
            {/each}
            {#if usableCredits.length == 0 && Object.keys(distributorCreditLists).length == 0}
              <p class="fizz-text-small fizz-inset-square-16">
                There are no credits available for this order.
              </p>
            {/if}
          </details>
        </div>

        <!-- Total -->
        <div class="fizz-space-between fizz-inset-square-16">
          <h4 class="fizz-heading-4">Total payment</h4>
          <div style="padding-right: 2rem">
            <strong><FormatMoney input={paymentTotal} formatType={"5"} /></strong>
          </div>
        </div>
      </div>

      <div class="fizz-flex-item">
        <section class="fizz-stack-32">
          <p class="fizz-text-medium fizz-full-width fizz-stack-8">
            <strong>Payment Method</strong>
          </p>

          {#if $paymentInstrumentsStore.paymentInstruments.length > 0}
            <select
              class="fizz-select fizz-full-width fizz-stack-24"
              name="saved_payment_methods"
              id="saved_payment_methods"
              on:change={handlePaymentInstrumentSelection}
              bind:value={selectedPaymentInstrument}
            >
              <option value="" disabled>Select</option>
              {#each $paymentInstrumentsStore.paymentInstruments as paymentInstrument}
                <option
                  value={paymentInstrument}
                  selected={selectedPaymentInstrument &&
                    paymentInstrument.id == selectedPaymentInstrument.id}
                >
                  {paymentInstrument.nickname}
                </option>
              {/each}
              {#if $paymentInstrumentsStore.paymentInstruments.length < 10}
                <Authorization authName={"payment_instruments.create"}>
                  <option value={"newPaymentInstrument"}> -- Add new payment method</option>
                </Authorization>
              {/if}
            </select>
          {:else}
            <button
              class="button button--secondary-link fizz-stack-16"
              id="add_payment_method"
              disabled={!authorizationCheck("payment_instruments.create")}
              on:click|preventDefault={showAddPaymentModal}>Add payment method</button
            >
            {#if paidInvoices > 0}
              <p class="fizz-text-small fizz-stack-16">
                With the latest ProviPay updates, you will need to re-enter your payment
                information.
              </p>
            {/if}
          {/if}
          {#if selectedPaymentInstrument && selectedPaymentInstrument.instrument_type == "credit_card"}
            <div class="fizz-text-small fizz-flex-row fizz-space-between fizz-stack-24">
              <p>3% platform fee</p>
              <p><strong>+ <FormatMoney input={$InvoiceStore.fees} formatType={"5"} /></strong></p>
            </div>
          {/if}

          <div data-testid="date-picker">
            <p class="fizz-text-medium fizz-full-width fizz-stack-8">
              <strong>Pay On</strong>
            </p>
            <DatePicker
              bind:startDate={userSelectedDate}
              dateFormat={"MMM D, YYYY"}
              minDate={new Date().toLocaleDateString()}
              defaultButtonLabel={""}
              minWidth={"100%"}
              numberOfMonths={1}
              position={"bottom: 3rem; top: auto;"}
              selectButtonStyle
              singleMode
            />
          </div>
        </section>

        <footer class="fizz-flex-row fizz-justify-end">
          <button
            class="fizz-button-secondary"
            id="submit-payment"
            data-testid="submit-payment"
            disabled={!selectedPaymentInstrument || invoiceNotPayable}
            on:click|preventDefault|once={() => onSchedulePaymentWrapper()}
          >
            <p class="fizz-text-large">
              {#if paymentTotal != 0}
                Schedule payment for <FormatMoney input={paymentTotal} formatType={"5"} />
              {:else}
                Schedule payment with credits
              {/if}
            </p>
          </button>
        </footer>
      </div>
    </div>
  </div>
</Modal>

<AddPaymentInstrumentModal
  bind:open={showModals["ChoosePaymentTypeModal"]}
  bind:enteredMicrodepositFlow
  onCloseModals={onClosePaymentMethodModals}
  {onSavePaymentInstrument}
/>
